import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  NativeSelect,
  OutlinedInput,
  Stack,
  TextField,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Ti18nId } from 'hooks/useI18n';
import { setSearchQuery } from 'utils/searchQuery';
import { getNursingRecode } from 'apis/survey';
import { IPatientInfoSmall } from 'apis/admin/type';
import { IUpedateNursingRecord } from 'apis/survey/type';
import useClassSection from 'store/section/useClassSection';
import useUser from 'store/user/useUser';
import useNotification from 'hooks/useNotification';
import ClassSectionSelect from '../Assignment/ClassSectionSelect';

interface Props {
  totalCount: number;
  onRequired: (id: Ti18nId) => void;
  currentSearchType?: number;
  currentSortType?: number;
  currentSize?: number;
  currentStartDate?: string;
  currentEndDate?: string;
  setList: React.Dispatch<React.SetStateAction<IUpedateNursingRecord[]>>;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  patientInfo: IPatientInfoSmall;
  onPrint: () => void;
}

const ContentsHead = ({
  onRequired,
  currentSearchType,
  totalCount,
  currentSortType,
  currentSize = 20,
  currentStartDate,
  currentEndDate,
  setList,
  setTotalCount,
  setIsLoading,
  patientInfo,
  onPrint,
}: Props) => {
  const keywordRef = useRef<HTMLSelectElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const searchTypeRef = useRef<HTMLSelectElement>(null);
  const sortTypeRef = useRef<HTMLSelectElement>(null);
  const sizeRef = useRef<HTMLSelectElement>(null);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const { student_uuid: userId } = useUser();
  const { onResetClassSection } = useClassSection();
  const { onFail } = useNotification();

  const formatDateFromURL = (dateString: string | undefined) => {
    if (!dateString) return '';
    return dateString.split('T')[0];
  };

  const formatStartOfDay = (dateString: string) =>
    dateString ? `${dateString}T00:00:00` : '';
  const formatEndOfDay = (dateString: string) =>
    dateString ? `${dateString}T23:59:59` : '';

  const [startDate, setStartDate] = useState<string>(
    formatDateFromURL(currentStartDate)
  );
  const [endDate, setEndDate] = useState<string>(
    formatDateFromURL(currentEndDate)
  );

  const [isAllDates, setIsAllDates] = useState<boolean>(
    !currentStartDate && !currentEndDate
  );

  const hasBothDates = Boolean(startDate && endDate);

  const onSearch = () => {
    const keyword = keywordRef.current?.value;
    const searchType = searchTypeRef.current?.value;

    if (!keyword) return onRequired('REQUIRED.KEYWORD');
    if (searchType === '2' && isNaN(Number(keyword)))
      return onRequired('REQUIRED.NUMBER');
    if (searchType === '1' && !isNaN(Number(keyword)))
      return onRequired('REQUIRED.STRING');

    const s = startDateRef.current?.value || '';
    const e = endDateRef.current?.value || '';

    const queryParams: Record<string, string> = {
      page: '1',
      searchType: searchType || '',
      keyword: String(keyword),
    };

    if (s && e) {
      queryParams.start_date = formatStartOfDay(s);
      queryParams.end_date = formatEndOfDay(e);
    } else if (s || e) {
      if (s) queryParams.start_date = formatStartOfDay(s);
      if (e) queryParams.end_date = formatEndOfDay(e);
    }

    const query = setSearchQuery(queryParams);
    navigate(`${pathname}?${query}`);
  };

  const searchReset = () => {
    const size = sizeRef.current?.value || String(currentSize);

    onResetClassSection();
    setIsAllDates(true);
    setStartDate('');
    setEndDate('');

    if (startDateRef.current) startDateRef.current.value = '';
    if (endDateRef.current) endDateRef.current.value = '';

    setIsLoading(true);
    getNursingRecode({
      page: '1',
      patient_id: String(patientInfo.patient_id),
      user_id: Number(userId),
      size,
      class_section_id: -1,
    })
      .then(({ data }) => {
        setList(data.student_nursing_record_infos);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `Виртуал өвчтөний жагсаалтыг ачаалах боломжгүй.`;
        onFail(message, e);
      })
      .finally(() => setIsLoading(false));

    navigate('/professorAuth/record');
  };

  const onSort = () => {
    const sort = sortTypeRef.current?.value;
    const size = sizeRef.current?.value || String(currentSize);

    const s = startDateRef.current?.value || '';
    const e = endDateRef.current?.value || '';

    const queryParams: Record<string, string> = {
      page: '1',
      sort_method: sort || '',
      size,
    };

    if (s && e) {
      queryParams.start_date = formatStartOfDay(s);
      queryParams.end_date = formatEndOfDay(e);
    } else if (s || e) {
      if (s) queryParams.start_date = formatStartOfDay(s);
      if (e) queryParams.end_date = formatEndOfDay(e);
    }

    const query = setSearchQuery(queryParams);
    navigate(`${pathname}?${query}`);
  };

  const onDateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    dateType: 'start' | 'end'
  ) => {
    const newValue = e.target.value;

    let newStartDate = startDate;
    let newEndDate = endDate;

    if (dateType === 'start') {
      newStartDate = newValue;
    } else {
      newEndDate = newValue;
    }

    if (newStartDate && newEndDate && newStartDate > newEndDate) {
      [newStartDate, newEndDate] = [newEndDate, newStartDate];
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setIsAllDates(!newStartDate && !newEndDate);

    const sort = sortTypeRef.current?.value || String(currentSortType || '');
    const size = sizeRef.current?.value || String(currentSize);
    const queryParams: Record<string, string> = {
      page: '1',
      sort_method: sort,
      size,
    };

    if (newStartDate && newEndDate) {
      queryParams.start_date = formatStartOfDay(newStartDate);
      queryParams.end_date = formatEndOfDay(newEndDate);
    } else if (newStartDate || newEndDate) {
      if (newStartDate) queryParams.start_date = formatStartOfDay(newStartDate);
      if (newEndDate) queryParams.end_date = formatEndOfDay(newEndDate);
    }

    const query = setSearchQuery(queryParams);
    navigate(`${pathname}?${query}`);
  };

  const SearchKeyword = () => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{ width: '100%', justifyContent: 'space-between' }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <TextField
              type="date"
              size="small"
              value={startDate}
              onChange={e => onDateChange(e, 'start')}
              InputProps={{ sx: { fontSize: '12px', height: '33px' } }}
            />
            <Typography>~</Typography>
            <TextField
              type="date"
              size="small"
              value={endDate}
              onChange={e => onDateChange(e, 'end')}
              InputProps={{ sx: { fontSize: '12px', height: '33px' } }}
            />
          </div>
          {isAllDates ? (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', ml: 1 }}
            >
              (Бүх огноо)
            </Typography>
          ) : !hasBothDates ? (
            <Typography variant="caption" sx={{ color: 'error.main', ml: 1 }}>
              Хайх бол хоёр огноог хоёуланг нь сонгоно уу.
            </Typography>
          ) : null}
          <Typography>|</Typography>
          <ClassSectionSelect user_id={userId} patientInfo={patientInfo} />
          <Typography>|</Typography>
          <NativeSelect
            size="small"
            input={<OutlinedInput sx={{ fontSize: '12px' }} />}
            inputRef={sortTypeRef}
            defaultValue={currentSortType}
            onChange={onSort}
          >
            <option value={1}>Нэр (A–Z)</option>
            <option value={3}>Оюутны ID (өсөх)</option>
            <option value={4}>Оюутны ID (буурах)</option>
          </NativeSelect>
          <Typography>|</Typography>
          <NativeSelect
            size="small"
            input={<OutlinedInput sx={{ fontSize: '12px' }} />}
            inputRef={sizeRef}
            defaultValue={currentSize}
            onChange={onSort}
          >
            <option value={20}>Хуудсанд 20</option>
            <option value={50}>Хуудсанд 50</option>
            <option value={100}>Хуудсанд 100</option>
          </NativeSelect>
          <Typography>|</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <NativeSelect
            size="small"
            input={<OutlinedInput sx={{ fontSize: '12px', height: '33px' }} />}
            inputRef={searchTypeRef}
            defaultValue={currentSearchType}
          >
            <option value={1}>Нэр</option>
            <option value={2}>Оюутны ID</option>
          </NativeSelect>

          <TextField
            placeholder="Хайх"
            inputRef={keywordRef}
            sx={{
              input: {
                minHeight: 33,
                boxSizing: 'border-box',
                fontSize: '12px',
              },
            }}
          />
          <Button
            variant="contained"
            onClick={onSearch}
            sx={{ height: '33px', width: '100px', fontSize: '12px' }}
            disabled={!isAllDates && !hasBothDates}
          >
            Хайх
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <Box
      display="flex"
      gap="10px"
      justifyContent="space-between"
      flexDirection="column"
      sx={{ mb: 1.5 }}
    >
      <Stack direction="row" justifyContent="space-between">
        <span style={{ color: '#6A3A87', fontSize: '22px', fontWeight: '400' }}>
          Бүх сувилахуйн тэмдэглэл
        </span>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            variant="contained"
            onClick={searchReset}
            sx={{
              height: '33px',
              width: '100px',
              fontSize: '12px',
              lineHeight: 1,
            }}
          >
            Хайлт цэвэрлэх
          </Button>
          <Button
            variant="contained"
            onClick={onPrint}
            sx={{ height: '33px', width: '100px', fontSize: '12px' }}
          >
            Хэвлэх
          </Button>
        </div>
      </Stack>
      <SearchKeyword />
      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
        Нийт {totalCount}
      </Typography>
    </Box>
  );
};

export default ContentsHead;
